import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactReader } from "react-reader";
import api from "../api";

export default function BookViewerDialog({ open, onClose, title, pdfUrl, epubUrl, bookId }) {
  const [tab, setTab] = useState(0);
  const [epubLocation, setEpubLocation] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const hasPdf = Boolean(pdfUrl);
  const hasEpub = Boolean(epubUrl);

  const activeTab = useMemo(() => {
    if (hasPdf && hasEpub) return tab;
    if (hasPdf) return 0;
    return 1;
  }, [hasPdf, hasEpub, tab]);

  const showPdf = hasPdf && (!hasEpub || activeTab === 0);
  const showEpub = hasEpub && (!hasPdf || activeTab === 1);

  useEffect(() => {
    if (!open || !pdfUrl) {
      setPdfBlobUrl(null);
      return;
    }
    let objectUrl = null;
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const endpoint = bookId
          ? `admin/ai/reference-books/${bookId}/pdf`
          : null;
        if (endpoint) {
          const res = await api.get(endpoint, { responseType: "blob" });
          if (cancelled) return;
          objectUrl = URL.createObjectURL(res.data);
          setPdfBlobUrl(objectUrl);
        } else {
          // Direct URL fallback (same-origin or public)
          setPdfBlobUrl(pdfUrl);
        }
      } catch {
        if (!cancelled) setPdfBlobUrl(pdfUrl);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, pdfUrl, bookId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{title || "Book viewer"}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {hasPdf && hasEpub && (
          <Tabs value={activeTab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}>
            <Tab label="PDF" />
            <Tab label="EPUB" />
          </Tabs>
        )}
        {showPdf && (
          <Box sx={{ height: "75vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {pdfLoading ? (
              <CircularProgress />
            ) : pdfBlobUrl ? (
              <iframe
                title={title || "PDF viewer"}
                src={pdfBlobUrl}
                style={{ width: "100%", height: "100%", border: "none" }}
              />
            ) : (
              <Typography color="text.secondary">Failed to load PDF.</Typography>
            )}
          </Box>
        )}
        {showEpub && (
          <Box sx={{ height: "75vh", position: "relative" }}>
            <ReactReader
              url={epubUrl}
              location={epubLocation}
              locationChanged={(loc) => setEpubLocation(loc)}
              epubOptions={{ openAs: "epub" }}
            />
          </Box>
        )}
        {!hasPdf && !hasEpub && (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">No document available to preview.</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function BookFormatChips({ pdfUrl, epubUrl, size = "small" }) {
  if (!pdfUrl && !epubUrl) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {pdfUrl && <Button size={size} variant="outlined" sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}>PDF</Button>}
      {epubUrl && <Button size={size} variant="outlined" sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}>EPUB</Button>}
    </Box>
  );
}
